import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Icon
} from '@chakra-ui/react'
import { FiCheckCircle, FiArrowRight } from 'react-icons/fi'
import { SpotlightHeader } from '../components/Spotlight/SpotlightHeader'
import { SpotlightFooter } from '../components/Spotlight/SpotlightFooter'
import { ProfileInterviewForm, ProfileFormData } from '../components/ProfileInterviewForm'
import { AuthModal } from '../components/AuthModal'
import { apiConfig } from '../config/api'

export const ProfileFormPage: React.FC = () => {
  const navigate = useNavigate()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<ProfileFormData | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const pendingFormDataRef = useRef<ProfileFormData | null>(null)

  const handleFormSubmit = (data: ProfileFormData) => {
    // Store form data and show auth modal
    setPendingFormData(data)
    pendingFormDataRef.current = data
    setShowAuthModal(true)
  }

  const handleAuthSuccess = async (email: string, idToken?: string, formData?: ProfileFormData) => {
    const dataToSubmit = formData || pendingFormData
    if (!dataToSubmit) return

    setShowAuthModal(false)
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // TODO: In production, you would:
      // 1. Get the ID token from Firebase Client SDK
      // 2. Include it in the Authorization header
      // 3. Call the backend API to create the spotlight page

      // For now, we'll redirect to the main app with the form data
      // In production, you'd make an API call here:
      
      // const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight`, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Authorization': `Bearer ${idToken}`
      //   },
      //   body: JSON.stringify({
      //     name: pendingFormData.name,
      //     spotlight_about: pendingFormData.bio,
      //     specialties: [pendingFormData.specialty],
      //     // ... other fields
      //   })
      // })

      // For now, redirect to main app with encoded data
      const encodedData = encodeURIComponent(JSON.stringify(dataToSubmit))
      window.location.href = `https://livewello.com/coach/spotlight-form?data=${encodedData}&email=${encodeURIComponent(email)}`
      
      // Simulate success for demo
      // setSubmitSuccess(true)
      // setTimeout(() => {
      //   navigate('/get-started?success=true')
      // }, 2000)

    } catch (error: any) {
      setSubmitError(error.message || 'Failed to submit your profile. Please try again.')
      setIsSubmitting(false)
    }
  }

  const handleFormSave = (data: ProfileFormData) => {
    // Auto-save is handled by localStorage in the form component
    // This is just a callback for any additional save logic
  }

  // Listen for Google One Tap success
  useEffect(() => {
    const handleOneTapSuccess = (event: CustomEvent<{ email: string; idToken: string }>) => {
      // If we have pending form data, automatically submit
      const formData = pendingFormDataRef.current
      if (formData) {
        setPendingFormData(formData)
        // Pass form data directly to avoid race condition
        handleAuthSuccess(event.detail.email, event.detail.idToken, formData)
      }
    }

    window.addEventListener('googleOneTapSuccess', handleOneTapSuccess as EventListener)
    return () => {
      window.removeEventListener('googleOneTapSuccess', handleOneTapSuccess as EventListener)
    }
  }, [])

  if (submitSuccess) {
    return (
      <Box minH="100vh" display="flex" flexDirection="column" bg="white">
        <SpotlightHeader />
        <Box flex={1} display="flex" alignItems="center" justifyContent="center">
          <Container maxW="2xl">
            <VStack gap={6} textAlign="center" py={16}>
              <Icon as={FiCheckCircle} boxSize={16} color="green.500" />
              <Heading size="2xl" color="gray.900">
                Profile Submitted Successfully!
              </Heading>
              <Text fontSize="lg" color="gray.600">
                We're building your Spotlight page. You'll receive an email when it's ready for review.
              </Text>
            </VStack>
          </Container>
        </Box>
        <SpotlightFooter />
      </Box>
    )
  }

  return (
    <Box minH="100vh" display="flex" flexDirection="column" bg="white">
      <SpotlightHeader />
      
      <Box flex={1} py={{ base: 8, md: 12 }}>
        <Container maxW="6xl">
          <VStack gap={8} align="stretch">
            {/* Header */}
            <VStack gap={4} textAlign="center" mb={4}>
              <Heading size={{ base: '2xl', md: '3xl' }} color="gray.900">
                Build Your Spotlight Profile
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Complete the form below. Your answers will help us create a compelling Spotlight page and ad creatives.
              </Text>
            </VStack>

            {/* Error Alert */}
            {submitError && (
              <Box
                p={4}
                bg="red.50"
                borderWidth="1px"
                borderColor="red.200"
                borderRadius="md"
                color="red.700"
              >
                <VStack align="start" gap={1}>
                  <Heading size="sm" color="red.900">Submission Error</Heading>
                  <Text fontSize="sm">{submitError}</Text>
                </VStack>
              </Box>
            )}

            {/* Form */}
            <ProfileInterviewForm
              onSubmit={handleFormSubmit}
              onSave={handleFormSave}
            />

            {/* Info Box */}
            <Box
              p={6}
              bg="blue.50"
              borderWidth="1px"
              borderColor="blue.200"
              borderRadius="xl"
            >
              <VStack align="start" gap={2}>
                <Heading size="sm" color="blue.900">
                  What happens next?
                </Heading>
                <Text fontSize="sm" color="blue.700">
                  After you submit, you'll create an account (or sign in if you have one). 
                  We'll then build your Spotlight page and ad creatives based on your answers. 
                  You'll be able to review and approve everything before we go live.
                </Text>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      <SpotlightFooter />

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false)
          setPendingFormData(null)
        }}
        onAuthSuccess={handleAuthSuccess}
        mode="signup"
      />
    </Box>
  )
}

